/**
 * SNCKD. — Delivery Modal
 * PLZ-Validierung + Datum/Uhrzeit-Picker + Session-Speicherung
 * Überträgt Lieferdaten an Warenkorb, Checkout, Bestellmails
 */

(function () {
  'use strict';

  // ── PLZ-Bereiche ───────────────────────────────────────────────
  const PLZ_RANGES = [
    { city: 'Düsseldorf', min: 40210, max: 40629 },
    { city: 'Essen',      min: 45127, max: 45399 },
    // Köln folgt demnächst
  ];

  const SESSION_KEY = 'snckd_delivery';
  const MODAL_ID    = 'snckd-delivery-modal';

  // ── Hilfsfunktionen ────────────────────────────────────────────
  function getCityForPLZ(plz) {
    const n = parseInt(plz, 10);
    if (isNaN(n) || plz.length !== 5) return null;
    const found = PLZ_RANGES.find(r => n >= r.min && n <= r.max);
    return found ? found.city : null;
  }

  function getEarliestDelivery() {
    const now  = new Date();
    const cutoff = now.getHours() >= 16; // nach 16:00 = übernächster Tag
    let daysToAdd = cutoff ? 2 : 1;
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + daysToAdd);
    // Nur Sonntage überspringen — Samstage sind Liefertage (18–20 Uhr)
    while (date.getDay() === 0) {
      date.setDate(date.getDate() + 1);
    }
    return date;
  }

  function toISODate(date) {
    return date.toISOString().slice(0, 10);
  }

  function isSunday(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return d.getDay() === 0;
  }

  function isSaturday(dateStr) {
    const d = new Date(dateStr + 'T00:00:00');
    return d.getDay() === 6;
  }

  // ── Zeitfenster je nach Tag ────────────────────────────────────
  const WEEKDAY_SLOTS = [
    { value: '10:00–12:00', label: '10:00 – 12:00 Uhr' },
    { value: '11:00–13:00', label: '11:00 – 13:00 Uhr' },
    { value: '12:00–14:00', label: '12:00 – 14:00 Uhr', selected: true },
    { value: '13:00–15:00', label: '13:00 – 15:00 Uhr' },
    { value: '14:00–16:00', label: '14:00 – 16:00 Uhr' },
    { value: '15:00–17:00', label: '15:00 – 17:00 Uhr' },
    { value: '16:00–18:00', label: '16:00 – 18:00 Uhr' },
  ];

  const SATURDAY_SLOTS = [
    { value: '16:00–18:00', label: '16:00 – 18:00 Uhr', selected: true },
  ];

  function updateTimeSlotsForDate(dateStr, timeSelect) {
    const slots = isSaturday(dateStr) ? SATURDAY_SLOTS : WEEKDAY_SLOTS;
    timeSelect.innerHTML = slots
      .map(s => `<option value="${s.value}"${s.selected ? ' selected' : ''}>${s.label}</option>`)
      .join('');
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  // ── Session lesen / schreiben ──────────────────────────────────
  function loadSession() {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function saveSession(data) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
  }

  function clearSession() {
    sessionStorage.removeItem(SESSION_KEY);
  }

  // ── Warenkorb-Attribute setzen ─────────────────────────────────
  async function pushToCart(data) {
    if (!data) return;
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'Lieferstadt':  data.city,
            'Lieferdatum':  formatDate(data.date),
            'Lieferuhrzeit': data.timeSlot,
            'Liefer-PLZ':   data.plz,
          },
        }),
      });
    } catch (e) {
      // non-fatal
    }
  }

  // ── Modal HTML ─────────────────────────────────────────────────
  function buildModal() {
    const earliest = getEarliestDelivery();
    const minDate  = toISODate(earliest);

    // Max-Datum: 30 Liefertage voraus (Mo–Sa, kein Sonntag)
    const maxDate = (() => {
      let d = new Date(earliest);
      let count = 0;
      while (count < 30) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() !== 0) count++;
      }
      return toISODate(d);
    })();

    const modal = document.createElement('div');
    modal.id = MODAL_ID;
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-label', 'Liefergebiet prüfen');
    modal.innerHTML = `
      <div class="snckd-modal__backdrop"></div>
      <div class="snckd-modal__box">

        <div class="snckd-modal__header">
          <div class="snckd-modal__logo">
            <svg viewBox="0 0 493.9 121.5" height="22" aria-label="SNCKD." style="display:block">
              <polygon fill="#fff" points="103.5 17 130.6 17 161.4 55.2 161.4 17 190.1 17 190.1 103.6 164.4 103.6 132.2 63.6 132.2 103.6 103.5 103.6 103.5 17"/>
              <path fill="#fff" d="M194.3,60.5v-.2c0-25.9,20.2-45.2,46.1-45.2s31.9,9.3,39.2,22.5l-23.9,14c-3.3-6.3-7.8-10.5-15.7-10.5s-16.2,8.5-16.2,18.9v.2c0,11.3,6.7,19.2,16.2,19.2s12.8-4.5,16.3-10.9l23.9,13.5c-7.3,12.9-19.6,23.4-41,23.4s-44.9-18.1-44.9-44.9"/>
              <polygon fill="#fff" points="283.2 17 312.2 17 312.2 49.4 336.8 17 370.5 17 340.3 54.6 371.1 103.6 336.5 103.6 319.6 76.1 312.2 85.2 312.2 103.6 283.2 103.6 283.2 17"/>
              <path fill="#fff" d="M402.3,42.2v36.2h4.8c13.4,0,21.8-5.4,21.8-18v-.2c0-12.5-8.4-18-21.8-18h-4.8ZM373.4,17h33.2c34.4,0,51.6,16.8,51.6,42.6v.2c0,25.8-17.6,43.8-52.6,43.8h-32.2V17Z"/>
              <rect fill="#E6007D" x="460.1" y="79.3" width="24.5" height="24.3"/>
              <path fill="#E6007D" d="M95.7,66.6c-4.2-9.6-14.9-14.8-29.7-18.2-1.4-.2-3-.1-4.4.2-.9.2-1.6,1.2-5.3,1.3-2.9.1-6.7-1.7-9.5-2.6-3.9-1.2-7.8-2.2-11.7-3.4-1.1-.3-2.1-1.6-.9-3.1.6-.7,1.7-1,2.7-.7,5.7,1.6,11.5,3,17,5.1.9.4,2.1.1,2.6-.6.5-.7.4-2.2-.5-2.6-5.6-2.3-11.4-3.8-17.2-5.3-1-.3-1.7-1.7-1-2.7.7-1.1,2.2-1,3.3-.8,6,1.5,11.9,3.2,17.6,5.7,1,.4,2.2.3,2.8-.4.6-.7.2-1.5-.7-2-5.7-2.6-11.5-4.4-17.7-6-1-.3-1.7-1.6-1.2-2.4.5-.8,2-1.8,3.1-1.5,7.7,2,14.5,4.6,21.5,8.1,1.8.9,3.1,3.5,3.3,5.3.2,2.3,1.9,4.6,4,5.9l16.7-23.1c-10.6-8.4-23.5-12.6-41.3-12.6S15.1,26.3,15.1,43.7v.3c0,5.7,1.6,10.3,4.4,14,3.8,4.8,12.5,9.9,20.6,12.9.8.3,1.6.5,2.3.8,1.9-3.7,6-5.1,12.9-3.5,8.4,1.9,17.9,7.4,16.6,13-1.3,5.6-11.8,7.2-20.2,5.3-7.4-1.7-10.6-5.4-10.5-10.1-.9-.4-9.1-3.5-12.3-5.1L11.1,92.4c11.6,10.3,28.3,15.4,47,15.4s40.4-11.8,40.4-31v-.3c0-3.7-.7-7-1.9-9.9z"/>
            </svg>
          </div>
          <div class="snckd-modal__headline">Wohin dürfen wir liefern?</div>
          <p class="snckd-modal__sub">Wir liefern nach Düsseldorf und Essen. Köln folgt demnächst.</p>
        </div>

        <form class="snckd-modal__form" id="snckd-delivery-form" novalidate>

          <div class="snckd-modal__field">
            <label for="snckd-plz">Postleitzahl <span class="snckd-modal__req">*</span></label>
            <input
              id="snckd-plz"
              name="plz"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{5}"
              maxlength="5"
              placeholder="z.B. 40210"
              autocomplete="postal-code"
              required
            >
            <div class="snckd-modal__error" id="snckd-plz-error" role="alert" aria-live="polite"></div>
          </div>

          <div class="snckd-modal__field">
            <label for="snckd-city">Lieferstadt</label>
            <select id="snckd-city" name="city">
              <option value="Düsseldorf" selected>Düsseldorf</option>
              <option value="Essen">Essen</option>
              <option value="Köln" disabled>Köln (demnächst)</option>
            </select>
          </div>

          <div class="snckd-modal__field">
            <label for="snckd-date">Wunschlieferdatum <span class="snckd-modal__req">*</span></label>
            <input
              id="snckd-date"
              name="date"
              type="date"
              min="${minDate}"
              max="${maxDate}"
              value="${minDate}"
              required
            >
            <div class="snckd-modal__hint" id="snckd-date-hint">Frühestmöglich: ${formatDate(minDate)}</div>
            <div class="snckd-modal__error" id="snckd-date-error" role="alert" aria-live="polite"></div>
          </div>

          <div class="snckd-modal__field">
            <label for="snckd-time">Wunschuhrzeit <span class="snckd-modal__req">*</span></label>
            <select id="snckd-time" name="time" required>
              <option value="10:00–12:00">10:00 – 12:00 Uhr</option>
              <option value="11:00–13:00">11:00 – 13:00 Uhr</option>
              <option value="12:00–14:00" selected>12:00 – 14:00 Uhr</option>
              <option value="13:00–15:00">13:00 – 15:00 Uhr</option>
              <option value="14:00–16:00">14:00 – 16:00 Uhr</option>
              <option value="15:00–17:00">15:00 – 17:00 Uhr</option>
              <option value="16:00–18:00">16:00 – 18:00 Uhr</option>
            </select>
          </div>

          <div class="snckd-modal__actions">
            <button type="submit" class="snckd-modal__btn-primary">
              Zum Shop →
            </button>
          </div>

          <div class="snckd-modal__notice">
            <span class="snckd-modal__notice-icon">◆</span>
            <strong>Mo–Fr:</strong> Bestellung bis 16:00 → nächster Werktag.<br>
            <strong>Samstag:</strong> Lieferung 16:00–18:00 Uhr.
          </div>

        </form>
      </div>
    `;
    return modal;
  }

  // ── Styles einfügen ────────────────────────────────────────────
  function injectStyles() {
    if (document.getElementById('snckd-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'snckd-modal-styles';
    style.textContent = `
      #snckd-delivery-modal {
        position: fixed; inset: 0; z-index: 99999;
        display: none; align-items: center; justify-content: center;
        padding: 16px;
      }
      #snckd-delivery-modal.is-open { display: flex; }

      .snckd-modal__backdrop {
        position: absolute; inset: 0;
        background: rgba(0,0,0,0.75);
        backdrop-filter: blur(6px);
        -webkit-backdrop-filter: blur(6px);
        animation: snckd-fade-in 0.2s ease;
      }

      .snckd-modal__box {
        position: relative; z-index: 1;
        background: #0F0F0F;
        border: 1px solid rgba(255,255,255,0.1);
        width: 100%; max-width: 480px;
        max-height: 90vh;
        overflow-y: auto;
        animation: snckd-slide-up 0.25s cubic-bezier(0.2,0.8,0.3,1);
      }

      @keyframes snckd-fade-in { from { opacity:0; } to { opacity:1; } }
      @keyframes snckd-slide-up {
        from { transform: translateY(24px); opacity:0; }
        to   { transform: translateY(0);   opacity:1; }
      }

      .snckd-modal__header {
        padding: 32px 32px 24px;
        border-bottom: 1px solid rgba(255,255,255,0.08);
      }
      .snckd-modal__logo { margin-bottom: 20px; }
      .snckd-modal__headline {
        font-family: 'Bebas Neue', sans-serif;
        font-size: 38px;
        line-height: 1;
        color: #fff;
        letter-spacing: 0.01em;
        text-transform: uppercase;
        margin-bottom: 10px;
      }
      .snckd-modal__sub {
        font-size: 14px;
        line-height: 1.55;
        color: #888;
        font-family: 'Inter', sans-serif;
        margin: 0;
      }

      .snckd-modal__form { padding: 28px 32px 32px; }

      .snckd-modal__field {
        margin-bottom: 20px;
      }
      .snckd-modal__field label {
        display: block;
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px;
        letter-spacing: 0.2em;
        text-transform: uppercase;
        color: #888;
        margin-bottom: 8px;
      }
      .snckd-modal__req { color: #E6007D; }

      .snckd-modal__field input,
      .snckd-modal__field select {
        width: 100%;
        height: 52px;
        background: #111;
        border: 1px solid rgba(255,255,255,0.12);
        color: #fff;
        font-family: 'Inter', sans-serif;
        font-size: 15px;
        padding: 0 16px;
        outline: none;
        transition: border-color 0.15s;
        box-sizing: border-box;
        border-radius: 0 !important;
      }
      .snckd-modal__field input:focus,
      .snckd-modal__field select:focus {
        border-color: #E6007D;
        box-shadow: 0 0 0 2px rgba(230,0,125,0.15);
      }
      .snckd-modal__field select option { background: #111; }
      .snckd-modal__field input[type="date"]::-webkit-calendar-picker-indicator {
        filter: invert(1);
        cursor: pointer;
        opacity: 0.7;
      }
      .snckd-modal__field input[type="date"]::-webkit-calendar-picker-indicator:hover {
        opacity: 1;
      }

      .snckd-modal__error {
        font-size: 12px; color: #ff6b6b;
        font-family: 'Inter', sans-serif;
        margin-top: 6px; min-height: 16px;
      }
      .snckd-modal__hint {
        font-size: 11px; color: #888;
        font-family: 'JetBrains Mono', monospace;
        letter-spacing: 0.08em;
        margin-top: 6px;
      }

      .snckd-modal__actions { margin-top: 28px; }
      .snckd-modal__btn-primary {
        width: 100%; height: 56px;
        background: #E6007D; color: #000;
        border: none; cursor: pointer;
        font-family: 'Inter', sans-serif;
        font-weight: 700; font-size: 14px;
        letter-spacing: 0.1em; text-transform: uppercase;
        transition: transform 0.15s, box-shadow 0.15s;
        border-radius: 0 !important;
      }
      .snckd-modal__btn-primary:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(230,0,125,0.35);
        background: #ff1a8c;
      }

      .snckd-modal__notice {
        margin-top: 20px;
        padding: 14px 16px;
        border: 1px solid rgba(255,255,255,0.06);
        background: rgba(230,0,125,0.04);
        font-size: 13px; line-height: 1.55;
        color: #ccc; font-family: 'Inter', sans-serif;
      }
      .snckd-modal__notice-icon { color: #E6007D; margin-right: 8px; }
      .snckd-modal__notice strong { color: #fff; }
    `;
    document.head.appendChild(style);
  }

  // ── Modal öffnen / schließen ───────────────────────────────────
  let modalEl = null;

  function openModal(onSuccess) {
    injectStyles();
    if (!modalEl) {
      modalEl = buildModal();
      document.body.appendChild(modalEl);
      setupModalEvents(modalEl);
    }
    modalEl._onSuccess = onSuccess;
    modalEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const plzInput = modalEl.querySelector('#snckd-plz');
    if (plzInput) setTimeout(() => plzInput.focus(), 50);
  }

  function closeModal(success) {
    if (!modalEl) return;
    modalEl.classList.remove('is-open');
    document.body.style.overflow = '';
    if (success && modalEl._onSuccess) modalEl._onSuccess();
  }

  // ── Form-Validierung & Events ──────────────────────────────────
  function setupModalEvents(modal) {
    const form     = modal.querySelector('#snckd-delivery-form');
    const plzInput = modal.querySelector('#snckd-plz');
    const citySelect = modal.querySelector('#snckd-city');
    const dateInput  = modal.querySelector('#snckd-date');
    const plzError   = modal.querySelector('#snckd-plz-error');
    const dateError  = modal.querySelector('#snckd-date-error');
    const backdrop   = modal.querySelector('.snckd-modal__backdrop');

    // PLZ-Eingabe → Stadt automatisch erkennen
    plzInput.addEventListener('input', () => {
      const val = plzInput.value.replace(/\D/g, '');
      plzInput.value = val;
      plzError.textContent = '';
      if (val.length === 5) {
        const city = getCityForPLZ(val);
        if (city) {
          citySelect.value = city;
          plzError.textContent = '';
        } else {
          citySelect.value = '';
          plzError.textContent = 'Diese Postleitzahl liegt außerhalb unseres Liefergebiets.';
        }
      } else {
        citySelect.value = '';
      }
    });

    const timeSelect = modal.querySelector('#snckd-time');

    // Zeitfenster sofort für das vorausgewählte Datum setzen
    updateTimeSlotsForDate(dateInput.value, timeSelect);

    // Datum-Validierung: Sonntage überspringen, Zeitfenster anpassen
    dateInput.addEventListener('change', () => {
      dateError.textContent = '';
      if (isSunday(dateInput.value)) {
        // Automatisch auf Montag springen
        const d = new Date(dateInput.value + 'T00:00:00');
        d.setDate(d.getDate() + 1);
        dateInput.value = toISODate(d);
      }
      updateTimeSlotsForDate(dateInput.value, timeSelect);
    });

    // Backdrop-Klick = Schließen (ohne Erfolg)
    backdrop.addEventListener('click', () => closeModal(false));

    // ESC-Taste
    document.addEventListener('keydown', function onKey(e) {
      if (e.key === 'Escape') { closeModal(false); document.removeEventListener('keydown', onKey); }
    });

    // Formular absenden
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      plzError.textContent = '';
      dateError.textContent = '';

      const plz  = plzInput.value.trim();
      const city = citySelect.value;
      const date = dateInput.value;
      const time = timeSelect.value;

      // PLZ validieren
      if (plz.length !== 5 || !/^\d{5}$/.test(plz)) {
        plzError.textContent = 'Bitte gib eine 5-stellige Postleitzahl ein.';
        plzInput.focus();
        return;
      }
      const detectedCity = getCityForPLZ(plz);
      if (!detectedCity) {
        plzError.textContent = 'Diese Postleitzahl liegt außerhalb unseres Liefergebiets.';
        plzInput.focus();
        return;
      }

      // Datum validieren
      if (!date) {
        dateError.textContent = 'Bitte wähle ein Lieferdatum.';
        dateInput.focus();
        return;
      }
      if (isSunday(date)) {
        dateError.textContent = 'Sonntage sind leider kein Liefertag.';
        dateInput.focus();
        return;
      }

      const deliveryData = {
        city: detectedCity,
        plz,
        date,
        timeSlot: time,
        savedAt: new Date().toISOString(),
      };

      saveSession(deliveryData);
      pushToCart(deliveryData);
      updateDeliverySummaryUI(deliveryData);
      closeModal(true);
    });
  }

  // ── Delivery-Summary in Cart/Product aktualisieren ────────────
  function updateDeliverySummaryUI(data) {
    document.querySelectorAll('[data-snckd-delivery-city]').forEach(el => {
      el.textContent = data.city;
    });
    document.querySelectorAll('[data-snckd-delivery-date]').forEach(el => {
      el.textContent = formatDate(data.date);
    });
    document.querySelectorAll('[data-snckd-delivery-time]').forEach(el => {
      el.textContent = data.time || data.timeSlot;
    });
    document.querySelectorAll('[data-snckd-delivery-plz]').forEach(el => {
      el.textContent = data.plz;
    });
    document.querySelectorAll('.snckd-delivery-summary').forEach(el => {
      el.removeAttribute('hidden');
    });
  }

  // ── Trigger-Logik ──────────────────────────────────────────────
  function interceptCheckout() {
    // Checkout-Button abfangen (Warenkorbseite + Cart-Drawer)
    document.addEventListener('click', (e) => {
      if (loadSession()) return; // Lieferdaten bereits gesetzt
      const target = e.target.closest('[data-snckd-delivery-trigger], button[name="checkout"], .cart__checkout-button');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      openModal(() => {
        target.click();
      });
    }, true);
  }

  // ── Init ───────────────────────────────────────────────────────
  function init() {
    const session = loadSession();

    // Bestehende Session-Daten in UI anzeigen
    if (session) {
      updateDeliverySummaryUI(session);
      if (window.location.pathname.includes('/cart')) {
        pushToCart(session);
      }
    }

    // "Lieferung ändern" Button
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-snckd-delivery-change]');
      if (!btn) return;
      clearSession();
      openModal(() => {
        if (window.location.pathname.includes('/cart')) {
          window.location.reload();
        }
      });
    });

    // Checkout-Button immer abfangen (self-gating via loadSession())
    interceptCheckout();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API
  window.SNKCDDelivery = { open: openModal, clear: clearSession, load: loadSession };
})();
